// built-from: /challenge-landscape-surface v1 impl:challenge-landscape-surface role:implementation
// The challenge landscape (056-vision-capture-method, B2 — spec §6 output
// oracle "challenge cards with provenance-graded evidence chips and held-open
// conflicts; crux designation with reasoning + rejected candidates"; mockup
// STAGE ONE). Renders only on escalation — the parent decides visibility
// (confirmed stream/orientation tier, or standing landscape facts so legacy
// 054 obstacles are never orphaned).
//
// Card grammar is the shared kit's (054 language kept). Additions here:
//   - evidence chips: `<grade> · <text>`, grade named in TEXT and class —
//     never colour alone (spec check 4);
//   - legacy vision.obstacle facts render as challenge-family cards, their
//     crux toggle emitting { obstacleFactId } (054 pins stay green);
//   - the designated card's drill-in (details/summary — the second and LAST
//     disclosure level) shows the designation reasoning and every candidate,
//     chosen AND rejected (spec check 7: the crux has a history).

import React from 'react';
import { Target } from 'lucide-react';
import type { GatedFact, SurfaceEdit, SurfaceFocus } from '../../facilitation/surfaceBinding';
import { Composer, FactCard, textOf, type EmitResult } from './factCardKit';
import VisionPanel from './VisionPanel';

function contentString(fact: GatedFact, key: string): string {
  const value = fact.content[key];
  return typeof value === 'string' ? value : '';
}

const CruxHistory: React.FC<{
  crux: GatedFact;
  candidates: GatedFact[];
  challengeTextById: Map<string, string>;
}> = ({ crux, candidates, challengeTextById }) => {
  const chosenCandidate = candidates.find((c) => contentString(c, 'disposition') === 'chosen');
  const reasoning =
    contentString(crux, 'reasoning')
    || (chosenCandidate ? contentString(chosenCandidate, 'reasoning') : '');
  const rejected = candidates.filter((c) => contentString(c, 'disposition') === 'rejected');
  const open = candidates.filter((c) => contentString(c, 'disposition') === 'candidate');
  if (!reasoning && candidates.length === 0) return null;

  const nameOf = (candidate: GatedFact) =>
    challengeTextById.get(contentString(candidate, 'challengeFactId'))
    || contentString(candidate, 'challengeFactId');

  return (
    <details className="vsf-cruxwhy">
      <summary>
        Why this is the crux
        {rejected.length > 0
          ? ` · ${rejected.length} candidate${rejected.length === 1 ? '' : 's'} rejected`
          : ''}
      </summary>
      {reasoning && (
        <div className="vsf-cand vsf-cand--chosen">
          <strong>Chosen:</strong> {reasoning}
        </div>
      )}
      {open.map((candidate) => (
        <div key={candidate.factId} className="vsf-cand">
          Candidate: “{nameOf(candidate)}” ·{' '}
          <span className="vsf-cand-why">{contentString(candidate, 'reasoning')}</span>
        </div>
      ))}
      {rejected.map((candidate) => (
        <div key={candidate.factId} className="vsf-cand">
          Rejected: “{nameOf(candidate)}” ·{' '}
          <span className="vsf-cand-why">{contentString(candidate, 'reasoning')}</span>
        </div>
      ))}
    </details>
  );
};

export interface ChallengeLandscapeProps {
  /** Challenges + legacy obstacles, in log order. */
  cards: GatedFact[];
  evidence: GatedFact[];
  candidates: GatedFact[];
  crux?: GatedFact;
  sealed: boolean;
  busy: boolean;
  lastTurn: Set<string>;
  /** Derived by the surface from the active act (owner-approved redesign,
   * 2026-07-09): expanded until frame-building; the chevron overrides. */
  defaultExpanded: boolean;
  /** The active act carries the head tint. */
  active: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}

const ChallengeLandscape: React.FC<ChallengeLandscapeProps> = ({
  cards,
  evidence,
  candidates,
  crux,
  sealed,
  busy,
  lastTurn,
  defaultExpanded,
  active,
  registerRef,
  onEmitEdit,
  onPublishFocus,
}) => {
  const cruxTargetId = crux
    ? contentString(crux, 'challengeFactId') || contentString(crux, 'obstacleFactId')
    : '';
  const challengeTextById = new Map(cards.map((c) => [c.factId, textOf(c)]));
  const cruxText = cruxTargetId ? challengeTextById.get(cruxTargetId) || '' : '';

  return (
    <VisionPanel
      panelId="vsf-landscape"
      testId="vsf-landscape"
      title="Challenges"
      subLine="What stands in the way, and how well we know it."
      countPill={
        cards.length > 0
          ? `${cards.length}${cruxText ? ' · 1 crux' : ''}`
          : undefined
      }
      sealed={sealed}
      active={active}
      defaultExpanded={defaultExpanded}
      strip={{
        label: 'Challenges',
        done: true,
        summary: (
          <>
            <span>{cards.length} captured</span>
            {cruxText && (
              <span className="vpanel-crux-tag">
                <Target size={11} aria-hidden="true" /> crux: {cruxText}
              </span>
            )}
          </>
        ),
      }}
    >
      {cards.map((card) => {
        const designated = Boolean(cruxTargetId) && card.factId === cruxTargetId;
        const chips = evidence.filter(
          (e) => contentString(e, 'challengeFactId') === card.factId,
        );
        return (
          <FactCard
            key={card.factId}
            fact={card}
            noun={card.type === 'vision.obstacle' ? 'obstacle' : 'challenge'}
            sealed={sealed}
            frozen={busy}
            pulsing={lastTurn.has(card.factId)}
            cruxToggle={{
              designated,
              input:
                card.type === 'vision.obstacle'
                  ? { obstacleFactId: card.factId }
                  : { challengeFactId: card.factId },
            }}
            detail={
              chips.length > 0 ? (
                <div className="vsf-evi">
                  {chips.map((chip) => {
                    const grade = contentString(chip, 'provenance') || 'stated';
                    return (
                      <span key={chip.factId} className={`vsf-prov vsf-prov--${grade}`}>
                        {grade} · {textOf(chip)}
                      </span>
                    );
                  })}
                </div>
              ) : undefined
            }
            drillIn={
              designated && crux ? (
                <CruxHistory
                  crux={crux}
                  candidates={candidates}
                  challengeTextById={challengeTextById}
                />
              ) : undefined
            }
            registerRef={registerRef}
            onEmitEdit={onEmitEdit}
            onPublishFocus={onPublishFocus}
          />
        );
      })}

      {!sealed && (
        <Composer
          label="Add a challenge"
          placeholder="e.g. Month-end close spans four teams"
          disabled={busy}
          onCommit={(text) =>
            onEmitEdit({ capabilityId: 'vision.add_challenge', input: { text } })
          }
        />
      )}
    </VisionPanel>
  );
};

export default ChallengeLandscape;
